import { PayloadAction } from '@reduxjs/toolkit'
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import * as bulkOperationsActions from '../actions/bulkOperationsActions'
import * as global from '../actions/globalActions'
import { ApiServiceInstance } from '../services/ApiService'
import { Errors } from '../services/ErrorMessageService'
import { AxiosError } from 'axios'
import { BulkOperationDownloadRequest, BulkOperationRequest, BulkOperationStatusRequest } from '../api/bulkOperations'
import { downloadFileFromBlob } from '../utils/file'

function wait(ms: number) {
  var start = new Date().getTime();
  var end = start;
  while (end < start + ms) {
    end = new Date().getTime();
  }
}

function mapBulkOperationRequestToFormData(request: BulkOperationRequest): FormData {
  const formData = new FormData();
  formData.append('operationType', request.operationType);
  formData.append("dataset", request.dataset ?? "");
  if (request.parameters) {
    Object.entries(request.parameters).forEach(([key, value]) => {  
      formData.append(`parameters.${key}`, value as any);
    });
  }
  return formData;
}

function* bulkExecute(action: PayloadAction<BulkOperationRequest>): Generator<any, void, any> {
  try {
    yield put(global.showSpinner())
    const formData = mapBulkOperationRequestToFormData(action.payload);
    const headers = {
        "Content-Type": "multipart/form-data"
    }
    
    let response = yield call(ApiServiceInstance.postWithHeader, 'bulkOperations/execute', formData, headers);
    let operationId = response.data.operationId;
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("operationId", operationId);
    window.location.search = urlParams.toString();
    
  } catch (e: any) {
    console.error(e);
    let errorMessage = ''
    if (e.response?.data?.exception) {
      errorMessage = e.response.data.message + ": " + e.response.data.exception
    }
    else if (e.response?.data) {
      errorMessage = e.response.data;
    }
    else if (e.response?.exception) {
      errorMessage = e.response.message + ": " + e.response.exception
    }
    else {
      errorMessage = (e as AxiosError).message;
    }
    yield put(Errors.toErrorAction(e, errorMessage))
  } finally {
    yield put(global.hideSpinner())
  }
}

function* fetchBulkOperationStatus(action: PayloadAction<BulkOperationStatusRequest>): Generator<any, void, any> {
  const POLLING_INTERVAL_MS = 1500
  const operationId = action.payload.operationId
  const statusUrl = `bulkOperations/${operationId}/status`;
  
  try {
    let response = yield call(ApiServiceInstance.get, statusUrl);
    yield put(bulkOperationsActions.bulkOperationStatusResponse({...response}));

    while (response.isCompleted === false) {
      response = yield call(ApiServiceInstance.get, statusUrl);
      yield put(bulkOperationsActions.bulkOperationStatusResponse({...response}));
      
      if (response.isCompleted == false) {
        wait(POLLING_INTERVAL_MS);
      }
    }
  } catch (e: any) {
    console.error(e);
    let errorMessage = ''
    if (e.message){
      errorMessage = e.message
    } else if (e.response?.data?.exception) {
      errorMessage = e.response.data.message + ": " + e.response.data.exception
    }
    else if (e.response?.data) {
      errorMessage = e.response.data;
    }
    else if (e.response?.exception) {
      errorMessage = e.response.message + ": " + e.response.exception
    }
    else {
      errorMessage = (e as AxiosError).message;
    }
    yield put(Errors.toErrorAction(e, errorMessage))
  }
}

function* bulkDownloadResults(action: PayloadAction<BulkOperationDownloadRequest>): Generator<any, void, any> {
  try {
    yield put(global.showSpinner())
    let response = yield call(ApiServiceInstance.get, 'bulkOperations/'+ action.payload.operationId + '/csv?status=' + (action.payload.status ?? ''));
    let fileName = 'BulkOperation_' + action.payload.operationId + (action.payload.status ? '_' + action.payload.status : '') + '.csv';
    yield call(downloadFileFromBlob, new Blob([response], { type: 'text/csv;charset=utf-8;' }), fileName);

  } catch (e: any) {
    console.error(e);
    let errorMessage = ''
    if (e.response?.data?.exception) {
      errorMessage = e.response.data.message + ": " + e.response.data.exception
    }
    else if (e.response?.data) {
      errorMessage = e.response.data;
    }
    else if (e.response?.exception) {
      errorMessage = e.response.message + ": " + e.response.exception
    }
    else {
      errorMessage = (e as AxiosError).message;
    }
    yield put(Errors.toErrorAction(e, errorMessage))
  } finally {
    yield put(global.hideSpinner())
  }
}

export default function* root() {
  yield takeLatest(bulkOperationsActions.bulkOperationRequest, bulkExecute)
  yield takeLatest(bulkOperationsActions.bulkOperationStatusRequest, fetchBulkOperationStatus)
  yield takeLatest(bulkOperationsActions.bulkOperationDownloadResultRequest, bulkDownloadResults)
}